import { useContext } from 'react'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSync, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAgencyPortal, { useAgencyPortalForm } from '@hooks/useAgencyPortal'
import useCurrentUser from '@hooks/useCurrentUser'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { confirmDelete } from '@functions/entity'

import EntitySelector from '@components/EntitySelector'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import type { AgencyPortalModel } from '@models/types'

const MODAL_KEY = 'CreateOrEditAgencyPortalModal'

type ModalCallbacks = {
  closeModal: () => void,
  createAgencyPortal: (payload: HandleCallbackActionParams) => Promise<object>,
  generateAgencyPortalAuthToken: (payload: HandleCallbackActionParams) => Promise<object>,
  updateAgencyPortal: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ConfirmGenerateTokenParams = {
  generateAgencyPortalAuthToken: ModalCallbacks['generateAgencyPortalAuthToken'],
  generateAuthToken: Function,
}

const confirmGenerateToken = (params: ConfirmGenerateTokenParams) => {
  const { generateAgencyPortalAuthToken, generateAuthToken } = params

  const generateAgencyPortalAuthTokenPayload = {
    callbacks: {
      action: generateAuthToken,
    },
    toastText: 'Token Generated',
  }

  swal
    .fire({
      confirmButtonColor: '#43AC6A',
      confirmButtonText: 'Yes, generate it.',
      icon: 'warning',
      showCancelButton: true,
      text: 'Are you sure? This will replace the existing authentication token.',
      title: 'Generate Token?',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        generateAgencyPortalAuthToken(generateAgencyPortalAuthTokenPayload)
      }
    })
}

type CreateOrUpdateAgencyPortalParams = {
  callbacks: ModalCallbacks,
  createFn: Function,
  entityState: Partial<AgencyPortalModel>,
  updateFn: Function,
}

const createOrUpdateAgencyPortal = (params: CreateOrUpdateAgencyPortalParams) => {
  const {
    callbacks, createFn, entityState, updateFn,
  } = params

  const { closeModal, createAgencyPortal, updateAgencyPortal } = callbacks || {}

  const text = entityState.id ? 'Updated' : 'Added'
  const actionFn = entityState.id ? updateFn : createFn

  const agencyPortalPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_agency_portal_auth_token: true,
      include_agency_portal_portal: true,
    },
    toastText: `Portal ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return updateAgencyPortal(agencyPortalPayload)

  // @ts-expect-error TS2345
  return createAgencyPortal(agencyPortalPayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditAgencyPortalModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, generateAgencyPortalAuthToken } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    agencyPortal: initAgencyPortal,
    assignedPortalIds,
  } = modalPayload

  const agencyPortalPayload = useAgencyPortal(initAgencyPortal)
  const {
    agencyPortal,
    callbacks: {
      createAgencyPortal: createFn,
      deleteAgencyPortal: deleteFn,
      generateAuthToken,
      updateAgencyPortal: updateFn,
    },
    creating,
    deleting,
    updating,
  } = agencyPortalPayload

  const agencyPortalFormPayload = useAgencyPortalForm(agencyPortal, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      agency_id,
      authentication_token,
      id,
      identity_code,
      options: { force_xml_agency },
      portal_id,
    },
    handlers,
    saveEnabled,
    setEntityState,
  } = agencyPortalFormPayload

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: { deleteString: 'remove' as const, entityName: 'Portal' },
  }

  const { isAgencyAdmin } = useCurrentUser()

  const updateOrCreateButtonText = id ? 'Save Portal' : 'Create Portal'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Edit' : 'Add'}
        titleSecondLine={!initAgencyPortal.agency_id ? 'Agency' : 'Portal'}
      />

      <SidebarModal.Content>
        <ScopedConditionalContent permittedRoleTitles={['Office Administrator']}>
          <SidebarNoticeBox>
            <SidebarNoticeBox.Title>Important Notice</SidebarNoticeBox.Title>

            <SidebarNoticeBox.Paragraph>
              Please do not update any of these values if you are not sure what they do.
              Changing these values could break current portal integrations.
            </SidebarNoticeBox.Paragraph>
          </SidebarNoticeBox>
        </ScopedConditionalContent>

        <Form>
          <EntitySelector
            callbacks={{
              selectItem: agency => setEntityState({ agency_id: agency ? agency.id : null }),
            }}
            disabled={!!id}
            entityKey="agencies"
            label="* Agency"
            selectedItemId={agency_id}
          />
          <EntitySelector
            callbacks={{
              selectItem: portal => setEntityState({ portal_id: portal ? portal.id : null }),
            }}
            disabled={!!id}
            entityKey="portals"
            label="* Portal"
            marginTop="large"
            selectedIds={assignedPortalIds}
            selectedItemId={portal_id}
          />

          <Form.Field label="* Identity Code" marginTop="large">
            <input name="identity_code" type="text" value={identity_code} {...handlers} />
          </Form.Field>

          {id && (
            <Form.Field label="Authentication Token" marginTop="large">
              <input disabled={!isAgencyAdmin} value={authentication_token} />
            </Form.Field>
          )}

          <Form.Field label="Force XML Agency" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              data-value-type="boolean"
              name="options.force_xml_agency"
              value={force_xml_agency}
              {...handlers}
            >
              <option value="false">No (Default)</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          {id && (
            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              <Form.Field label="More Options" marginTop="large">
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faSync} />}
                  loading={updating}
                  onClick={() => confirmGenerateToken({ generateAgencyPortalAuthToken, generateAuthToken })}
                  size="medium"
                >
                  Regenerate Token
                </Button>

                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  loading={deleting}
                  // @ts-expect-error TS2322
                  marginTop="medium"
                  onClick={() => confirmDelete(confirmDeletePayload)}
                  size="medium"
                >
                  Remove Portal
                </Button>
              </Form.Field>
            </ScopedConditionalContent>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateAgencyPortal({
            callbacks, createFn, entityState, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAgencyPortalModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
